<template>
  <FileSelectorWrapper
    ref="fileSelector"
    v-slot="{ openFileSelector }"
    v-bind="$attrs"
    :disabled="disabled || loading"
    :class="classes"
    :accept="accept"
    @select-files="handleFileSelect"
    @select-invalid-files="handleChoosingInvalidFiles"
  >
    <div
      v-loading="loading"
      v-dragndrop
      class="droppable-file-selector__dropzone"
      data-drop-class="droppable-file-selector__dropzone--dropping"
      @drop="!disabled && handleFileDrop($event)"
    >
      <div class="droppable-file-selector__content">
        <slot :open-file-selector="openFileSelector">
          <img
            class="droppable-file-selector__icon"
            :src="dragAndDropIcon"
            alt=""
          />

          <span class="droppable-file-selector__inline-hint">
            {{ $t('OR') }}
          </span>

          <p
            v-dompurify-html="$t('DROPPABLE_FILE_SELECTOR.HINT')"
            class="droppable-file-selector__hint"
          />

          <ElButton
            :disabled="disabled"
            type="primary"
            @click="openFileSelector"
          >
            {{ $t('DROPPABLE_FILE_SELECTOR.CHOOSE_FILE') }}
          </ElButton>
        </slot>
      </div>

      <div
        v-if="errorMessage"
        v-dompurify-html="errorMessage"
        class="droppable-file-selector__error-message text-latin"
      />
    </div>
  </FileSelectorWrapper>
</template>

<script>
import FileSelectorWrapper from './FileSelectorWrapper';
import dragndrop from '@/directives/dragndrop';
import dragAndDropIcon from '@/assets/images/dragAndDropIcon.svg';
import { hasValidExtension } from '@/utils/file-utils/file-validators';

export default {
  name: 'DroppableFileSelector',
  directives: { dragndrop },
  components: { FileSelectorWrapper },
  props: {
    accept: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorMessage: null,
    };
  },
  computed: {
    classes() {
      return {
        'droppable-file-selector': true,
        'droppable-file-selector--invalid': !!this.errorMessage,
        'droppable-file-selector--inline': this.inline,
      };
    },
    dragAndDropIcon() {
      return dragAndDropIcon;
    },
  },
  methods: {
    handleFileDrop(event) {
      this.errorMessage = null;
      const { files = [] } = event.dataTransfer || event.target;
      const filesArray = Array.from(files);
      const validFiles = [];
      const invalidFiles = [];

      filesArray.forEach(file => {
        if (hasValidExtension(file, this.accept)) validFiles.push(file);
        else invalidFiles.push(file);
      });

      this.handleChoosingInvalidFiles(invalidFiles);
      this.$emit('change', validFiles);
    },
    handleFileSelect(files) {
      this.errorMessage = null;
      this.$emit('change', files);
    },
    handleChoosingInvalidFiles(invalidFiles = []) {
      if (!invalidFiles.length) return;

      const fileNames = invalidFiles
        .map(({ name }) => `<span>${name}</span>`)
        .join('');

      if (invalidFiles.length === 1) {
        this.errorMessage = this.$t('DROPPABLE_FILE_SELECTOR.INVALID_FILE', {
          fileName: fileNames,
        });
      } else {
        this.errorMessage = this.$t('DROPPABLE_FILE_SELECTOR.INVALID_FILES', {
          fileNames,
        });
      }
    },
    reset() {
      this.errorMessage = null;
      this.$refs.fileSelector?.reset();
    },
  },
};
</script>

<style lang="scss">
.droppable-file-selector {
  $block: &;
  height: inherit;
  text-align: center;
  border-radius: 5px;
  border: 2px dashed $--input-border-color;

  &--invalid {
    border-color: $lb-color-alert;
  }

  &__error-message {
    font-size: $lb-small-font-size;
    color: $lb-color-alert;
    padding-bottom: $global-padding;

    span {
      text-decoration: underline;
      font-weight: bold;
      font-style: italic;
      margin: $global-margin / 3;
    }
  }

  &__content {
    height: inherit;
    min-height: rem-calc(290px);
    padding: $global-padding;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  &__dropzone {
    height: inherit;
    transition: all 0.25s ease-out;

    &--dropping {
      background: #eee;
    }
  }

  &__icon {
    pointer-events: none;
    user-select: none;
  }

  &__hint {
    margin: $global-margin * 2 0;
    color: #888;
    pointer-events: none;
    user-select: none;
  }

  &__inline-hint {
    margin: 0 $global-margin * 2;
    pointer-events: none;
    user-select: none;
    color: #888;
    display: none;
  }

  &__progress-wrp {
    width: 50%;
    text-align: center;
  }

  &__progress {
    max-width: 450px;
    direction: ltr;
    text-align: left;
    padding-left: 10px;
    margin-bottom: $global-margin;

    .el-progress-bar__inner {
      background-color: $--color-info;

      &.is-success {
        background-color: $lb-color-primary;
      }
    }
  }

  &--inline {
    #{$block}__content {
      min-height: rem-calc(100px);
      flex-direction: row;
    }

    #{$block}__hint {
      display: none;
    }

    #{$block}__inline-hint {
      display: block;
    }

    #{$block}__icon {
      width: 4rem;
      pointer-events: none;
      user-select: none;
    }
  }

  @include breakpoint(medium down) {
    &__content {
      min-height: 0;
    }

    &__icon,
    &__hint,
    &__inline-hint {
      display: none !important;
    }
  }
}
</style>
